export const en = {
  modules: [
    {
      template: 'authorModule',
      text: `<p>
              Article Written by <strong>Safy-Hallan Farah</strong>
              Article Edited by <strong>Sean Conroy</strong>
              Films Directed by <strong>Mohammad Gorjestani</strong>
              Photography by <strong>Mohammad Gorjestani</strong> & <strong>Liban Mahamoud</strong>    
            </p>`
    },
    {
      template: 'leadModule',
      subtitle: '',
      body: `“We’re the first wave of Somali-Americans mixing in with American culture. We have a lot that we’re trying to adapt to, assimilate, figure out, in terms of just who we are and what our culture is.”`
    },
    {
      template: 'textModule',
      text: `<p>
                Nuzzled between downtown Minneapolis and the neighborhood of Midtown, the Riverside Plaza apartments are home to immigrant families of Ethiopian, Vietnamese, and Somali descent, along with other ethnicities. But it’s the Somali-Americans, like me, who have become synonymous with the tall, blocky buildings and surrounding area, known as the Cedar-Riverside neighborhood. Statistically, we’re the most prominent minority group in South Minneapolis. But ultimately, it’s less about metrics and more about our blackness and Muslimness: black and Muslim bodies are the most surveilled bodies in America.  
              </p>
              <p>
              “Mogadishu, Minnesota” and “Little Mogadishu” are just two of the well-intentioned but otherizing misnomers outsiders have for us, given the neighborhood. But Somali-Americans don’t live exclusively in one insular pocket of South Minneapolis; the 70,000-plus Somali-born residents of the Twin Cities and the surrounding metro area are an integrated, everyday presence all over the city.
             </p>`
    },
    {
      template: 'imageGalleryModule',
      images: [
        {
          width: 426,
          height: 325,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery1-1.jpg',
          alt: 'Cedar-Riverside apartments in Minneapolis',
          index: '01',
          caption: `<p>Cedar-Riverside apartments.</p>`
        },
        {
          width: 426,
          height: 325,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery1-2.jpg',
          alt: 'Teenagers girls playing basketball in the Cedar-Riverside neighborhood',
          index: '02',
          caption: `<p>Teenagers girls playing basketball in the Cedar-Riverside neighborhood.</p>`
        },
        {
          width: 426,
          height: 640,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery1-3.jpg',
          alt: 'Teenage boys hanging out on a bench in the Cedar-Riverside neighborhood',
          index: '03',
          caption: `<p>The boys are hanging nearby.</p>`
        }
      ]
    },
    {
      template: 'textModule',
      text: `<p>
                In 1991, a violent civil war in Somalia displaced millions of children, women, and men. The Somali diaspora began with many fleeing—often by foot or boat—to neighboring countries as a method of survival. As a result, my people are now scattered all over this planet; I have friends and relatives everywhere from New Zealand to Norway. Minnesota, with one of the most robust refugee resettlement programs in the United States, was an ideal, welcoming place for Somali refugees and asylees to resettle.
            </p>
            <p>
                Many Somalis I know once lived with the expectation that they would return to Somalia, that their residence in America was only temporary. After all, Somalia was one of the safest, most tolerant, most culturally rich countries in Africa in the decades following independence from Britain. But, in time, Minnesota became more than a short-term pitstop; Minnesota is home. And for younger Somali-Americans, especially, a “return to Somalia” has never been in the cards because America is all we’ve ever known. It’s where we live, pay taxes, and consume hotdish. We’re not going anywhere because we simply <em>love</em> America. 
            </p>`
    },
    {
      template: 'videoModule',
      index: '01',
      caption: '<p>Abdulkadir and Bihi eat dinner and discuss immigration and life as a Somali American.</p>',
      posterImage: {
        width: 1440,
        height: 810,
        url: 'assets/images/story/story-twin-cities/twin-cities-video-poster1.jpg'
      },
      videoURL:
        'https://player.vimeo.com/external/345161565.sd.mp4?s=4d33df621e15e63a6db482154326417170d32793&profile_id=165'
    },
    {
      template: 'quoteModule',
      quote: `When I first got here, I was 52 years old. I am now 74...I think. I’m not one of the kids who were born or grew up here. So I still dream of Somalia. That’s the country I was raised in and its memory will never leave me. And to America, I say thank you, because it’s the place that welcomed us.`,
      cite: 'Abdhulkadir, 74, Lyft Driver'
    },
    {
      template: 'imageGalleryModule',
      images: [
        {
          width: 426,
          height: 426,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery2-1.jpg',
          alt: 'A group praying at a mosque.',
          index: '01',
          caption: `<p>Prayer at a local mosque.</p>`
        },
        {
          width: 426,
          height: 640,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery2-2.jpg',
          alt: 'Two Somali American women at Karmel Mall.',
          index: '02',
          caption: `<p>Two Somali American women at Karmel Mall.</p>`
        },
        {
          width: 426,
          height: 641,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery2-3.jpg',
          alt: 'A Somali American couple in front of a curtain with Somali flag colors',
          index: '03',
          caption: `<p>The Somali flag colors, proudly displayed.</p>`
        }
      ]
    },
    {
      template: 'textModule',
      text: `<p>
                Somalis are one of the most civically engaged new immigrant groups in the country. We produced Congress’s rising star, Ilhan Omar. Ms. Omar, a Minnesota resident, straddles the line between two distinct Somali-American generations with different values and lived experiences—and differing portrayals in the media. Depictions of older Somali-Americans emphasizing their religiosity are plentiful. So too are portraits of the misconduct of low-income male youth. The HBO series <em>Mogadishu, Minnesota</em>, produced by Kathryn Bigelow, was refused permission to film at the Riverside Plaza apartments because the script was rumored to depict a Somali terror cell in Minnesota. Minnesotans protested during filming, and, ultimately, HBO decided not to move forward with the show. But whether on television, film, digital media, or in print, very rarely is the “regular-ness” of Somali-Americans (of someone like myself!) recognized, highlighted, or written about.            
             </p>`
    },
    {
      template: 'videoModule',
      index: '01',
      caption: '<p>Abdi on being Somali and American.</p>',
      posterImage: {
        width: 1440,
        height: 810,
        url: 'assets/images/story/story-twin-cities/twin-cities-video-poster2.jpg'
      },
      videoURL:
        'https://player.vimeo.com/external/345123120.sd.mp4?s=366fa8fd3c25f751900b00ca74eb915cab60ee6b&profile_id=165'
    },
    {
      template: 'quoteModule',
      quote: `We’ve all read Catcher in the Rye in high school, we’ve all had to do all these things that every American has done. I’ve got cousins who play hockey like every day. And what’s more Minnesotan than playing hockey?`,
      cite: 'Abdi, 25, Lyft Driver'
    },
    {
      template: 'textModule',
      text: `<p>
              To remedy what I saw as a gap in media representation for younger Somali-Americans like myself, I launched the first issue of a zine called <em>1991</em> last year. The title is a reference to the year Somalia’s civil war began, and I chose it because the civil war remains a phantom presence in the lives of many across the Somali diaspora. The year is jam-packed with historical and cultural significance, too. And while Somalis’ stories—especially the ones I want to tell—don’t start or end there, the year was a watershed moment for us globally. (Also, palindromes promote aesthetic symmetry, so I might have geeked out at the chance to design with the name of the publication being <em>1991</em> in mind.)   
            </p>
            <p>
              In the first issue’s “Letter from the Editor,” I listed the cultural changes I want to see happen in my lifetime: more art by Somalis; millennial and Gen Z Somali kids taking back the power from our elders; and Somali writers, cultural producers, and artists exerting great influence over our portrayal in wider American culture. The latter goal has inspired me to pivot my creative focus from journalism to film and television production; as dominant cultural mediums, these are where the images of Somalis are so often misrepresented.
             </p>`
    },
    {
      template: 'imageGalleryModule',
      images: [
        {
          width: 426,
          height: 640,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery3-1.jpg',
          alt: 'Three young Somali American women at a bowling alley',
          index: '01',
          caption: `<p>Somali-American youth at the local bowling alley.</p>`
        },
        {
          width: 426,
          height: 640,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery3-2.jpg',
          alt: 'A teenage boy gets a haircut',
          index: '02',
          caption: `<p>Youth culture.</p>`
        },
        {
          width: 426,
          height: 325,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery3-3.jpg',
          alt: 'Mercedes Iman backstage',
          index: '03',
          caption: `<p>Mercedes Iman, a local Muslim drag queen who recently appeared on RuPaul's Drag Race.</p>`
        }
      ]
    },
    {
      template: 'textModule',
      text: `<p>
                But I’m not solely interested in positive imagery, as I don’t think representation exists strictly within the binary of “positive” and “negative.” I think the nuanced representation of people of color, particularly Somalis, starts with having the agency to tell our own stories. I’m usually one to wince at loaded buzzwords like <em>representation</em> and <em>inclusion</em>—especially when they’re employed in personal essays like this one. But I know firsthand of the material consequences of misrepresentation: impaired social mobility, discrimination in multiple forms, and self-image issues, to name a few. I’ve seen the material consequences of Hollywood’s sloppy portrayal of Somalis, from <em>Black Hawk Down</em> (starring Minnesota’s very own Josh Hartnett!) to <em>Captain Phillips</em>, and I’ve written about it for outlets like <em>Shondaland</em> and the <em>New York Times</em>. With every dehumanizing portrayal, a little piece of our humanity is stripped away. 
            </p>`
    },
    {
      template: 'imageGalleryModule',
      images: [
        {
          width: 500,
          height: 750,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery4-1.jpg',
          alt: 'A young man standing up through a sunroof in Cedar-Riverside',
          index: '01',
          caption: `<p>A summer evening in Cedar-Riverside.</p>`
        },
        {
          width: 500,
          height: 749,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery4-2.jpg',
          alt: 'A young woman looks at the camera at sunset',
          index: '02',
          caption: `<p>Maryam, at sunset.</p>`
        }
      ]
    },
    {
      template: 'videoModule',
      index: '01',
      caption: '<p>Maryam on her identity as a Somali American woman.</p>',
      posterImage: {
        width: 1440,
        height: 810,
        url: 'assets/images/story/story-twin-cities/twin-cities-video-poster3.jpg'
      },
      videoURL:
        'https://player.vimeo.com/external/345123388.sd.mp4?s=308a228ef391664a2d5573e93bbda0f49b84d007&profile_id=165'
    },
    {
      template: 'quoteModule',
      quote: `I’m Muslim first. It’s my guiding light to life. That’s the thing I like about Islam too, is because there’s no color in Islam. God first. And then I’m Somali, because that’s my blood. And then I’m American because I grew up here. And then I’m a woman.`,
      cite: 'Maryam, 26, Lyft Driver'
    },
    {
      template: 'textModule',
      text: `<p>
                As for <em>1991</em>, I’m eager to continue our work. I’m also eager to parlay the experience of working on this project into writing and producing films via the creative studio and production company I founded last summer, Studio 91. Creating new images of Somalis—ones that aren’t interpolated with the white gaze or mired in racism—will be my life’s work, <em>inshallah</em>.
            </p>`
    },
    {
      template: 'donateModule',
      title: 'Make A Donation',
      subtitle: 'And join our efforts',
      body: `<p> 
                Every issue contributing to our current immigration crisis is interconnected. Your stories inspire us to continue to take a stand — when our community of riders and drivers is threatened, we’re encouraged to take action. 
                <br><br>
                In honor of the Somali-American community, we are humbled to support the <a href="https://immigrationforum.org/" target="_blank">National Immigration Forum</a>. For 30 years, the Forum has worked to advance sound federal immigration solutions through its policy expertise, communications outreach and coalition building work, which forges powerful alliances of diverse constituencies across the country to build consensus on the important role of immigrants in America.
                <br><br>
                Lyft is honored to offer transportation support for National Immigration Forum’s vital mission. Please join us in supporting them.
             </p>`,
      cta: 'Make A Donation',
      url:
        'https://nationalimmigrationforum.secure.nonprofitsoapbox.com/forum-donate?utm_source=Lyft&utm_medium=Button&utm_campaign=Lyft%20launch'
    },
    {
      template: 'nextStoryModule',
      id: 'story-cesar',
      title: 'Nine Numbers',
      location: 'Nashville, TN',
      alt: 'Cesar in his room',
      description: 'Undocumented, but Not Invisible',
      imageSize1URL: 'assets/images/carousel/carousel-image-cesar-1280.jpg',
      imageSize2URL: 'assets/images/carousel/carousel-image-cesar-1920.jpg',
      featureVideoURL:
        'https://player.vimeo.com/external/344699878.sd.mp4?s=7ed73e4052345395d4a29f2d53f2ff61fe2d38de&profile_id=165',
      rolloverVideoURL:
        'https://player.vimeo.com/external/344700772.sd.mp4?s=e8b4b3719f9e4e2fd0904403aa5e037bb9f18fec&profile_id=165'
    }
  ]
};

export const es = {
  modules: [
    {
      template: 'authorModule',
      text: `<p>
              Artículo escrito por <strong>Safy-Hallan Farah</strong>
              Artículo editado por <strong>Sean Conroy</strong>
              Videos dirigidos por <strong>Mohammad Gorjestani</strong>
              Fotografía de <strong>Mohammad Gorjestani</strong> y <strong>Liban Mahamoud</strong>    
            </p>`
    },
    {
      template: 'leadModule',
      subtitle: '',
      body: `"Somos la primera ola de estadounidenses somalíes que se mezcla con la cultura estadounidense. Tenemos muchas cosas a las cuales tratamos de adaptarnos, asimilar, descubrir, en términos de quiénes somos y qué es nuestra cultura".`
    },
    {
      template: 'textModule',
      text: `<p>
                Hospedados entre el centro de Minéapolis y el vecindario de Midtown, los departamentos de Riverside Plaza albergan a familias de inmigrantes etíopes, vietnamitas y somalíes, junto con otras etnias. Pero son los somalíes-estadounidenses, como yo, quienes se han convertido en el referente de los bloques de edificios altos y el área aledaña, conocido como el vecindario de Cedar-Riverside. Estadísticamente, somos el grupo minoritario más prominente en el área. Pero al final, se trata menos de métricas y más sobre nuestra raza y religión: los cuerpos negros y musulmanes son los cuerpos más vigilados en los Estados Unidos. 
              </p>
              <p>
              "Mogadishu, Minnesota" y "Little Mogadishu" son solo dos de los nombres que aunque no son malintencionados resultan inapropiados que le han puesto a nuestro barrio. Pero los somalíes-estadounidenses no viven exclusivamente en un lugar insular del sur de Mineápolis; los más de 70,000 residentes de las Ciudades Gemelas y el área metropolitana aledaña nacidos en Somalia son una presencia integrada y cotidiana en toda la ciudad.
             </p>`
    },
    {
      template: 'imageGalleryModule',
      images: [
        {
          width: 426,
          height: 325,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery1-1.jpg',
          alt: 'Departamentos Cedar-Riverside en Mineápolis',
          index: '01',
          caption: `<p>Departamentos Cedar-Riverside.</p>`
        },
        {
          width: 426,
          height: 325,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery1-2.jpg',
          alt: 'Niñas adolescentes jugando básquetbol en el barrio de Cedar-Riverside.',
          index: '02',
          caption: `<p>Niñas adolescentes jugando básquetbol en el barrio de Cedar-Riverside.</p>`
        },
        {
          width: 426,
          height: 640,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery1-3.jpg',
          alt: 'Adolescentes reunidos en una banca en el barrio Cedar-Riverside',
          index: '03',
          caption: `<p>Los chicos están reunidos cerca.</p>`
        }
      ]
    },
    {
      template: 'textModule',
      text: `<p>
                En 1991, una violenta guerra civil en Somalia desplazó a millones de niños, mujeres y hombres. El éxodo somalí empezó con muchos huyendo, con frecuencia a pie o en barco, a los países vecinos como método de supervivencia. Como resultado, mi gente ahora está dispersa por todo el planeta. Tengo amigos y familiares en todas partes, desde Nueva Zelanda hasta Noruega. Minnesota cuenta con uno de los programas de reasentamiento de refugiados más sólidos de los Estados Unidos, resultó un lugar ideal y acogedor para que los refugiados y asilados somalíes se reasentaran.
            </p>
            <p>
                Muchos de los somalíes que conozco vivían con la esperanza de poder volver a Somalia, que su residencia en Estados Unidos era solo temporal. Después de todo, Somalia fue uno de los países más seguros, tolerantes y culturalmente ricos de África tras su independencia de Gran Bretaña. Pero, al final, Minnesota no fue una parada a corto plazo; Minnesota es nuestro hogar. Y para los jóvenes somalíes-estadounidenses, especialmente, un "regreso a Somalia" nunca ha sido una posibilidad porque Estados Unidos es todo lo que conocemos. Es donde vivimos, pagamos impuestos y consumimos hotdish. No nos vamos a ir a ningún lado porque, simplemente, <em>amamos</em> a Estados Unidos. 
            </p>`
    },
    {
      template: 'videoModule',
      index: '01',
      caption:
        '<p>Abdulkadir y Bihi cenan y conversan sobre la inmigración y sus vidas como somalíes-estadounidenses.</p>',
      posterImage: {
        width: 1440,
        height: 810,
        url: 'assets/images/story/story-twin-cities/twin-cities-video-poster1.jpg'
      },
      videoURL:
        'https://player.vimeo.com/external/358160034.sd.mp4?s=c837c27a9103f29d7a258a5c4a3fc9af079087e3&profile_id=165'
      // videoSubtitles: [
      //   {label: 'Spanish', lang: 'es', src: 'assets/subtitles/twin_cities_1.vtt'}
      // ]
    },
    {
      template: 'quoteModule',
      quote: `Cuando llegué por primera vez, tenía 52 años. Ahora tengo 74... creo. No soy uno de los niños que nacieron o crecieron aquí. Así que todavía sueño con Somalia. Ese es el país donde me criaron y nunca me dejará su recuerdo. Y a Estados Unidos le doy las gracias, porque es el lugar que nos dio la bienvenida`,
      cite: 'Abdhulkadir, 74, conductor de Lyft'
    },
    {
      template: 'imageGalleryModule',
      images: [
        {
          width: 426,
          height: 426,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery2-1.jpg',
          alt: 'Un grupo rezando en una mezquita.',
          index: '01',
          caption: `<p>Oración en una mezquita local.</p>`
        },
        {
          width: 426,
          height: 640,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery2-2.jpg',
          alt: 'Dos mujeres somalíes-estadounidenses en el centro comercial Karmel.',
          index: '02',
          caption: `<p>Dos mujeres somalíes-estadounidenses en el centro comercial Karmel.</p>`
        },
        {
          width: 426,
          height: 641,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery2-3.jpg',
          alt: 'Una pareja somalí-estadounidense frente a una cortina con los colores de bandera somalí',
          index: '03',
          caption: `<p>Los colores de la bandera somalí mostrados con orgullo.</p>`
        }
      ]
    },
    {
      template: 'textModule',
      text: `<p>
                Los somalíes son uno de los nuevos grupos de inmigrantes con mayor participación cívica en el país. Produjimos a Ilhan Omar, la radiante estrella del Congreso. La Sra. Omar, residente de Minnesota, representa a dos generaciones distintas de somalíes-estadounidenses con diferentes valores, experiencias de vida y, también, distintas representaciones en los medios de comunicación. Son muchas las representaciones de los somalíes-estadounidenses mayores que enfatizan su religiosidad. También son muchos los retratos que se centran en la mala conducta del grupo de varones jóvenes de bajos ingresos. A la serie de HBO <em>Mogadishu, Minnesota</em>, producida por Kathryn Bigelow, se le negó el permiso para filmar en los departamento de Riverside Plaza porque se rumoraba que el guión representaba una célula terrorista somalí en Minnesota. Los ciudadanos protestaron durante el rodaje y, al final, HBO decidió no seguir con el programa. Pero ya sea en televisión, cine, medios digitales o impresos, es raro que se reconozca, resalte o escriba sobre la "regularidad" de los somalíes-estadounidenses (de alguien como yo).            
             </p>`
    },
    {
      template: 'videoModule',
      index: '01',
      caption: '<p>Abdi hablando de cómo es ser somalí y estadounidense.</p>',
      posterImage: {
        width: 1440,
        height: 810,
        url: 'assets/images/story/story-twin-cities/twin-cities-video-poster2.jpg'
      },
      videoURL:
        'https://player.vimeo.com/external/345123120.sd.mp4?s=366fa8fd3c25f751900b00ca74eb915cab60ee6b&profile_id=165',
      videoSubtitles: [
        {label: 'Spanish', lang: 'es', src: 'assets/subtitles/twin_cities_2.vtt'}
      ]
    },
    {
      template: 'quoteModule',
      quote: `Todos hemos leído Catcher in the Rye en la escuela, todos hemos tenido que hacer todas las cosas que todo estadounidense ha hecho. Tengo primos que juegan al hockey todos los días. Y, ¿qué representa más a Minnesota que el hockey?`,
      cite: 'Abdi, 25, conductor de Lyft'
    },
    {
      template: 'textModule',
      text: `<p>
              Para remediar lo que vi como una brecha en la representación de los jóvenes somalíes-estadounidenses como yo en los medios de comunicación, lancé el primer número de una publicación llamada <em>1991</em> el año pasado. El título hace referencia al año en que comenzó la guerra civil en Somalia, y lo elegí porque la guerra civil sigue siendo una presencia que persigue a muchos exiliados. Ese año también está impregnado de gran importancia histórica y cultural. Y mientras las historias de los somalíes, especialmente las que quiero contar, no empiezan ni terminan allí, el año fue un momento decisivo para nosotros a escala mundial. (Además, los palíndromos promueven la simetría estética, por lo que es posible que me haya gustado la oportunidad de diseñar alrededor de <em>1991</em> como título).   
            </p>
            <p>
              En la "Carta del editor" del primer número, enumeré los cambios culturales que quiero que sucedan en mi vida: más arte de los somalíes; que los milenials y la generación Z somalí recuperen el poder que actualmente tienen los mayores; y escritores, productores culturales y artistas somalíes que ejerzan una gran influencia sobre nuestra representación más amplia en la cultura estadounidense. Este último objetivo me ha inspirado para cambiar mi enfoque creativo del periodismo a la producción de cine y televisión; como medios culturales dominantes y aquí es donde las imágenes de los somalíes son a menudo mal representadas. 
             </p>`
    },
    {
      template: 'imageGalleryModule',
      images: [
        {
          width: 426,
          height: 640,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery3-1.jpg',
          alt: 'Tres mujeres somalíes-estadounidenses jóvenes en un boliche',
          index: '01',
          caption: `<p>Jóvenes somalíes-estadounidenses en el boliche local.</p>`
        },
        {
          width: 426,
          height: 640,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery3-2.jpg',
          alt: 'Un adolescente se corta el pelo',
          index: '02',
          caption: `<p>La cultura de los jóvenes en exhibición.</p>`
        },
        {
          width: 426,
          height: 325,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery3-3.jpg',
          alt: 'Mercedes Iman detrás del escenario',
          index: '03',
          caption: `<p>Mercedes Iman, una drag queen musulmana local que recientemente apareció en RuPaul's Drag Race.</p>`
        }
      ]
    },
    {
      template: 'textModule',
      text: `<p>
                Pero no solo me interesan las imágenes positivas, ya que no creo que la representación exista estrictamente dentro del código binario de lo "positivo" y lo "negativo". Creo que la representación matizada de las personas de color, en particular los somalíes, empieza con tener la libertad de contar nuestras propias historias. Por lo general, soy una persona que se retuerce con el uso excesivo de palabras de moda como <em>representación</em> e <em>inclusión</em>, especialmente cuando se usan en ensayos personales como este. Pero conozco personalmente las consecuencias materiales de la mala representación: una movilidad social disminuida, discriminación en múltiples formas y problemas de autoimagen, por nombrar algunos. He visto las consecuencias materiales de la representación descuidada de los somalíes por parte de Hollywood, desde <em>Black Hawk Down</em> (¡protagonizada por el propio Josh Hartnett de Minnesota!), hasta <em>Captain Phillips</em>, y he escrito sobre esto para medios como <em>Shondaland</em> y el <em>New York Times</em>. Con cada retrato deshumanizado que aparece, nos despojan de una pequeña parte de nuestra humanidad vital. 
            </p>`
    },
    {
      template: 'imageGalleryModule',
      images: [
        {
          width: 500,
          height: 750,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery4-1.jpg',
          alt: 'Un joven saliendo por un techo vehicular en Cedar-Riverside',
          index: '01',
          caption: `<p>Una tarde de verano en Cedar-Riverside.</p>`
        },
        {
          width: 500,
          height: 749,
          url: 'assets/images/story/story-twin-cities/twin-cities-image-gallery4-2.jpg',
          alt: 'Una joven mira la cámara al atardecer',
          index: '02',
          caption: `<p>Maryam al atardecer.</p>`
        }
      ]
    },
    {
      template: 'videoModule',
      index: '01',
      caption: '<p>Maryam sobre su identidad como una mujer somalí-estadounidense.</p>',
      posterImage: {
        width: 1440,
        height: 810,
        url: 'assets/images/story/story-twin-cities/twin-cities-video-poster3.jpg'
      },
      videoURL:
      'https://player.vimeo.com/external/345123388.sd.mp4?s=308a228ef391664a2d5573e93bbda0f49b84d007&profile_id=165',
      videoSubtitles: [
        {label: 'Spanish', lang: 'es', src: 'assets/subtitles/twin_cities_3.vtt'}
      ]
    },
    {
      template: 'quoteModule',
      quote: `Primero, soy musulmana. Es la luz que me guía en la vida. Eso es lo que también me gusta del Islam, porque no hay color en el Islam. Dios primero. Y luego soy somalí, porque esa es mi sangre. Y luego soy estadounidense porque crecí aquí. Y luego soy mujer.`,
      cite: 'Maryam, 26, conductora de Lyft'
    },
    {
      template: 'textModule',
      text: `<p>
                En cuanto a <em>1991</em>, estoy ansiosa por continuar nuestro trabajo. También estoy ansiosa por compartir la experiencia de trabajar en este proyecto, de escribir y producir videos a través del estudio creativo y la casa productora que fundé el verano pasado, Studio 91. Crear nuevas imágenes de somalíes, que no estén interpoladas con la mirada blanca o sumidas en el racismo, será el trabajo de mi vida, <em>inshallah</em>.
            </p>`
    },
    {
      template: 'donateModule',
      title: 'Haz una donación',
      subtitle: 'Unete a nuestra causa',
      body: `<p> 
                Todos los problemas que contribuyen a nuestra actual crisis de inmigración están interconectados. Tus historias nos inspiran a seguir tomando una postura: cuando nuestra comunidad de viajeros y conductores sea amenazada, nosotros tomaremos las medidas pertinentes. 
                <br><br>
                En honor a la comunidad somalí-estadounidense, nos sentimos honrados de apoyar al <a href="https://immigrationforum.org/" target="_blank">Foro Nacional de Inmigración</a>. Por más de 30 años, el Foro ha trabajado para impulsar soluciones de inmigración a nivel federal que sean sólidas a través de su experiencia en políticas, divulgación de comunicación y trabajo de formación de coaliciones, que forja alianzas poderosas de diversos constituyentes en todo el país y así crea un consenso sobre la importancia del papel que juegan los inmigrantes en los Estados Unidos.
                <br><br>
                Lyft se enorgullece de ofrecer transporte para que el Foro Nacional de Inmigración siga realizando su vital misión. Por favor, únete a nosotros para apoyarlos.
             </p>`,
      cta: 'Haz una donación',
      url:
        'https://nationalimmigrationforum.secure.nonprofitsoapbox.com/forum-donate?utm_source=Lyft&utm_medium=Button&utm_campaign=Lyft%20launch'
    },
    {
      template: 'nextStoryModule',
      id: 'story-cesar',
      title: 'Nueve números',
      location: 'Nashville, TN',
      alt: 'Cesar en su habitacion',
      description: 'Sin documentos, pero no invisible',
      imageSize1URL: 'assets/images/carousel/carousel-image-cesar-1280.jpg',
      imageSize2URL: 'assets/images/carousel/carousel-image-cesar-1920.jpg',
      featureVideoURL:
        'https://player.vimeo.com/external/344699878.sd.mp4?s=7ed73e4052345395d4a29f2d53f2ff61fe2d38de&profile_id=165',
      rolloverVideoURL:
        'https://player.vimeo.com/external/344700772.sd.mp4?s=e8b4b3719f9e4e2fd0904403aa5e037bb9f18fec&profile_id=165'
    }
  ]
};

export default {en, es};
